import React from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CommonSelect from "../../../../core/common/commonSelect";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import type { Company, ViewType } from "../types";

interface TodoToolbarProps {
  view: ViewType;
  companies: Company[];
  selectedCompanyId: string | null;
  selectedCompanyType: string | null;
  onCompanySelect: (option: any) => void;
  onAddTodo: () => void;
  onOpenFilters: () => void;
  activeFiltersCount: number;
  /** Optional Export button (List view only). */
  onExport?: () => void;
  exportDisabled?: boolean;
}

/**
 * Breadcrumb + action bar shared by both Task views: list/grid toggle, Add Task,
 * company selector, filter trigger and (optionally) the export action.
 */
const TodoToolbar: React.FC<TodoToolbarProps> = ({
  view,
  companies,
  selectedCompanyId,
  selectedCompanyType,
  onCompanySelect,
  onAddTodo,
  onOpenFilters,
  activeFiltersCount,
  onExport,
  exportDisabled,
}) => {
  const selectedCompany = companies.find(
    (c) => c.id == selectedCompanyId && c.type == selectedCompanyType,
  );

  return (
    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
      <div className="my-auto mb-2">
        <h2 className="mb-1">📝 Task</h2>
        <nav>
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={all_routes.adminDashboard}>
                <i className="ti ti-smart-home" />
              </Link>
            </li>
            <li className="breadcrumb-item">Application</li>
            <li className="breadcrumb-item active" aria-current="page">
              Task
            </li>
          </ol>
        </nav>
      </div>
      <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
        {/* List / Grid view toggle (navigates between the two task routes) */}
        <div className="d-flex align-items-center border rounded p-1 me-2">
          <Link
            to={all_routes.TodoList}
            className={`btn btn-icon btn-sm ${
              view === "list" ? "active bg-primary text-white" : ""
            }`}
            title="List"
          >
            <i className="ti ti-list-tree" />
          </Link>
          <Link
            to={all_routes.todo}
            className={`btn btn-icon btn-sm ${
              view === "grid" ? "active bg-primary text-white" : ""
            }`}
            title="Grid"
          >
            <i className="ti ti-table" />
          </Link>
        </div>

        <div className="d-flex align-items-center me-2">
          <Link
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#add_todo"
            className="btn btn-primary d-flex align-items-center"
            onClick={onAddTodo}
          >
            <i className="ti ti-circle-plus me-2" />
            Add Task
          </Link>
        </div>

        <div
          className="d-flex align-items-center me-2"
          style={{ minWidth: "200px" }}
        >
          <CommonSelect
            className="select"
            options={companies.map((company) => ({
              value: `${company.id}-${company.type?.trim()}`,
              label: `${company.name} - ${company.type}`,
            }))}
            defaultValue={
              selectedCompanyId
                ? {
                    value: `${selectedCompanyId}-${selectedCompanyType}`,
                    label: `${selectedCompany?.name} - ${selectedCompany?.type}`,
                  }
                : undefined
            }
            onChange={onCompanySelect}
          />
        </div>

        <div className="d-flex align-items-center me-2">
          <button
            className="btn btn-outline-primary d-flex align-items-center position-relative"
            onClick={onOpenFilters}
            data-bs-target="#filterPanel"
          >
            <i className="ti ti-filter me-2" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="badge bg-primary rounded-pill ms-2">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {onExport && (
            <button
              type="button"
              className="btn btn-info ms-2"
              onClick={onExport}
              disabled={exportDisabled}
            >
              <i className="ti ti-download me-1" /> Export Todo
            </button>
          )}
        </div>

        <div className="d-flex align-items-center ms-2">
          <CollapseHeader />
        </div>
      </div>
    </div>
  );
};

export default TodoToolbar;
